// Import leads from a spreadsheet or a Facebook lead export (LEAD-06, LEAD-16, D-097). Every row is kept as an enquiry
// on its customer, with where it came from: the source, campaign, ad set, ad, form, when the customer enquired and the
// form's answers. Customers already known keep their data; importing the same file again adds nothing.
import { useState } from "react";
import {
  answerColumns,
  batches,
  count,
  decodeText,
  type FieldKey,
  FIELDS,
  guessMapping,
  importSummary,
  type Mapping,
  parseCsvLines,
  toCsv,
  toImportRows,
} from "../lib/csv";
import { fmtDateTime } from "../lib/format";
import type { AssignMode, ImportOptions, Member } from "../lib/types";
import { useMember } from "../app/session";
import { Card, errorMessage, ErrorNote, Page, useLoad } from "../app/ui";

/** Rows per request: about a second and a half on the server, far inside a request's 8 seconds. */
const BATCH = 250;

/** How the preview shows a time read from the file: as written when it has no offset (the company's own time). */
function whenRead(value: string | undefined, tz: string): string {
  if (!value) return "";
  if (!/^\d{4}-\d{2}-\d{2}T/.test(value)) return "not understood; the import time will be used";
  if (/(Z|[+-]\d{2}(:?\d{2})?)$/i.test(value)) return fmtDateTime(value, tz);
  return fmtDateTime(new Date(`${value}Z`).toISOString(), "UTC");
}

interface Outcome {
  inserted: number;
  updated: number;
  enquiries: number;
  repeats: number;
  /** Each skipped row's place in the parsed rows (for the download) and its line in the file (for the table). */
  problems: { index: number; line: number; phone: string | null; reason: string }[];
  notes: { line: number; note: string }[];
  /** Set when a batch failed: the rows before it are in. */
  stoppedAt?: string;
}

export function Import() {
  const { api, tz } = useMember();
  const members = useLoad(() => api.members(), [api]);
  const sources = useLoad(() => api.sources(), [api]);
  const imports = useLoad(() => api.leadImports(), [api]);
  const [rows, setRows] = useState<string[][] | null>(null);
  const [lines, setLines] = useState<number[]>([]);
  const [fileName, setFileName] = useState("");
  const [mapping, setMapping] = useState<Mapping | null>(null);
  const [sourceId, setSourceId] = useState("");
  const [mode, setMode] = useState<AssignMode>("one");
  const [chosen, setChosen] = useState<string[]>([]);
  const [progress, setProgress] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [error, setError] = useState<string | null>(null);

  const team = (members.data ?? []).filter((m) => m.status === "active");

  const load = async (file: File) => {
    setOutcome(null);
    setError(null);
    const bytes = new Uint8Array(await file.arrayBuffer());
    // An Excel workbook is a zip file: it has to be saved as CSV first.
    if (bytes[0] === 0x50 && bytes[1] === 0x4b) {
      setError("That is an Excel workbook. Save it as CSV first (in Excel: File, Save As, CSV), then choose it here.");
      return;
    }
    const parsed = parseCsvLines(decodeText(bytes));
    if (parsed.rows.length === 0) {
      setError("That file has no rows.");
      return;
    }
    const guessed = guessMapping(parsed.rows);
    setFileName(file.name);
    setRows(parsed.rows);
    setLines(parsed.lines);
    setMapping(guessed);
    const list = sources.data ?? [];
    const facebook = list.find((s) => /facebook/i.test(s.name));
    setSourceId((guessed.facebook && facebook ? facebook : list.find((s) => s.type === "import"))?.id ?? "");
    setMode(guessed.columns.owner_phone >= 0 ? "column" : "one");
  };

  const prepared = rows && mapping && mapping.columns.phone >= 0 ? toImportRows(rows, mapping, lines) : [];
  const dataRows = rows ? rows.length - (mapping?.hasHeader ? 1 : 0) : 0;
  const repeatedNumbers = (() => {
    const seen = new Map<string, number>();
    for (const p of prepared) {
      const key = p.row.phone.replace(/\D/g, "").slice(-10);
      seen.set(key, (seen.get(key) ?? 0) + 1);
    }
    return [...seen.values()].filter((n) => n > 1).length;
  })();
  const width = rows ? Math.max(...rows.slice(0, 50).map((r) => r.length)) : 0;
  const header = rows && mapping?.hasHeader
    ? Array.from({ length: width }, (_, i) => rows[0][i]?.trim() || `Column ${i + 1}`)
    : Array.from({ length: width }, (_, i) => `Column ${i + 1}`);
  const kept = rows && mapping ? answerColumns(rows, mapping) : [];
  const mapped = mapping ? FIELDS.filter((f) => mapping.columns[f.key] >= 0) : [];
  const ready = prepared.length > 0 && progress === null &&
    (mode === "none" || (mode === "column" && (mapping?.columns.owner_phone ?? -1) >= 0) ||
      ((mode === "one" || mode === "split") && chosen.length > 0));

  const start = async () => {
    if (!mapping) return;
    setError(null);
    setOutcome(null);
    const total: Outcome = { inserted: 0, updated: 0, enquiries: 0, repeats: 0, problems: [], notes: [] };
    const options: ImportOptions = {
      import_id: crypto.randomUUID(),
      file_name: fileName,
      format: mapping.facebook ? "facebook" : "csv",
      source_id: sourceId || null,
      assign: mode === "one" || mode === "split" ? { mode, members: chosen } : { mode },
    };
    const parts = batches(prepared, BATCH);
    for (const [n, part] of parts.entries()) {
      const range = `${n * BATCH + 1}–${n * BATCH + part.length}`;
      setProgress(`Importing ${range} of ${prepared.length}…`);
      try {
        const res = await api.importLeads(part.map((p) => p.row), options);
        total.inserted += res.inserted;
        total.updated += res.updated;
        total.enquiries += res.enquiries ?? 0;
        total.repeats += res.repeats ?? 0;
        for (const bad of res.invalid) {
          const at = part[bad.row];
          total.problems.push({ index: at?.index ?? -1, line: at?.line ?? 0, phone: bad.phone, reason: bad.reason });
        }
        for (const note of res.notes ?? []) total.notes.push({ line: part[note.row]?.line ?? 0, note: note.note });
      } catch (e) {
        total.stoppedAt = `Stopped at rows ${range}: ${errorMessage(e)}`;
        break;
      }
    }
    setOutcome(total);
    setProgress(null);
    imports.reload();
  };

  /** The skipped rows as they were in the file, with the reason, to fix and import again. */
  const downloadSkipped = () => {
    if (!rows || !outcome) return;
    const reasons = new Map(outcome.problems.map((p) => [p.index, p.reason]));
    const out = [[...header, "Why it was skipped"]];
    rows.forEach((r, i) => {
      const reason = reasons.get(i);
      if (reason) out.push([...header.map((_, c) => r[c] ?? ""), reason]);
    });
    const url = URL.createObjectURL(new Blob([toCsv(out)], { type: "text/csv" }));
    const a = Object.assign(document.createElement("a"), {
      href: url,
      download: `${fileName.replace(/\.[^.]+$/, "")}-skipped.csv`,
    });
    a.click();
    URL.revokeObjectURL(url);
  };

  const select = (key: FieldKey, label: string) => (
    <label className="field" key={key}>
      <span>{label}{key === "phone" ? " *" : ""}</span>
      <select
        value={mapping?.columns[key] ?? -1}
        onChange={(e) => {
          const column = Number(e.target.value);
          setMapping((m) => (m ? { ...m, columns: { ...m.columns, [key]: column } } : m));
          // With no owner column, "whoever the owner column names" would give every new customer to no one (D-100).
          if (key === "owner_phone" && column < 0 && mode === "column") setMode("one");
        }}
      >
        <option value={-1}>{key === "phone" ? "Choose a column" : "Not in the file"}</option>
        {header.map((h, i) => <option key={i} value={i}>{h}</option>)}
      </select>
    </label>
  );

  const names = (ids: string[]) =>
    ids.map((id) => team.find((m) => m.id === id)?.display_name).filter(Boolean).join(", ");

  return (
    <Page
      title="Import leads"
      sub="A Facebook lead export, or a sheet from Excel or Google Sheets saved as CSV. Every row is kept with where it came from."
    >
      <div className="stack">
        <Card title="1. Choose the file">
          <input
            type="file"
            accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values"
            onChange={(e) => e.target.files?.[0] && void load(e.target.files[0])}
          />
          {rows && mapping && (
            <p className="faint" style={{ marginTop: 8 }}>
              {fileName}: {dataRows} rows{mapping.facebook
                ? ". A Facebook lead export: its ads, ad sets, campaigns, forms and times are kept."
                : "."}
            </p>
          )}
          <ErrorNote error={error ?? sources.error} />
        </Card>

        {rows && mapping && (
          <Card title="2. Match the columns">
            <label className="row" style={{ marginBottom: 12 }}>
              <input
                type="checkbox"
                checked={mapping.hasHeader}
                onChange={(e) => setMapping({ ...mapping, hasHeader: e.target.checked })}
              />
              The first row has column names
            </label>
            <h3>The person</h3>
            <div className="grid cols-3">
              {FIELDS.filter((f) => f.part === "person").map((f) => select(f.key, f.label))}
            </div>
            <h3>Where the enquiry came from</h3>
            <div className="grid cols-3">
              {FIELDS.filter((f) => f.part === "source").map((f) => select(f.key, f.label))}
            </div>
            <p className="faint">
              {kept.length
                ? `Kept as the form's answers, shown on the customer's page: ${kept.map((i) => header[i]).join(", ")}.`
                : "Every column is matched."}
            </p>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    {mapped.map((f) => <th key={f.key}>{f.label}</th>)}
                    {kept.length > 0 && <th>Answers</th>}
                  </tr>
                </thead>
                <tbody>
                  {prepared.slice(0, 5).map((p) => (
                    <tr key={p.index}>
                      {mapped.map((f) => (
                        <td key={f.key}>
                          {(rows[p.index] ?? [])[mapping.columns[f.key]]}
                          {f.key === "enquired_at" && (rows[p.index] ?? [])[mapping.columns[f.key]]?.trim() && (
                            <div className="faint">Read as {whenRead(p.row.enquired_at, tz)}</div>
                          )}
                        </td>
                      ))}
                      {kept.length > 0 && <td className="faint">{p.row.answers?.length ?? 0}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {rows && mapping && (
          <Card title="3. Source and who calls them">
            <div className="grid cols-2">
              <label className="field">
                <span>Source</span>
                <select value={sourceId} onChange={(e) => setSourceId(e.target.value)}>
                  {(sources.data ?? []).filter((s) => s.active).map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                <span className="hint">
                  {mapping.columns.source >= 0
                    ? "Rows whose Source column names one of your sources keep it; the rest get this one."
                    : "Every lead in the file comes from this source."}
                </span>
              </label>
              <div className="field">
                <span>New customers go to</span>
                {(
                  [
                    ["one", "One person"],
                    ["split", "Several people, in turn"],
                    ...(mapping.columns.owner_phone >= 0 ? [["column", "Whoever the owner column names"]] : []),
                    ["none", "Nobody yet"],
                  ] as [AssignMode, string][]
                ).map(([value, label]) => (
                  <label className="row" key={value}>
                    <input
                      type="radio"
                      name="assign"
                      checked={mode === value}
                      onChange={() => {
                        setMode(value);
                        if (value === "one") setChosen((c) => c.slice(0, 1));
                      }}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            {mode === "one" && <PersonPicker team={team} chosen={chosen} onChange={setChosen} single />}
            {mode === "split" && <PersonPicker team={team} chosen={chosen} onChange={setChosen} />}
            <p className="faint">
              {mode === "none"
                ? "Leads nobody owns are on no one's phone until you give them to someone."
                : mode === "column"
                ? "Customers you already have keep their owner."
                : "They appear on their phone under New customers, newest enquiry first. Customers you already have keep their owner."}
            </p>
          </Card>
        )}

        {rows && mapping && (
          <Card title="4. Import">
            <p>
              {count(prepared.length, "row")} with a phone number will be imported
              {dataRows > prepared.length
                ? `; ${count(dataRows - prepared.length, "row")} without one ${
                  dataRows - prepared.length === 1 ? "is" : "are"
                } left out`
                : ""}.
              {repeatedNumbers > 0 &&
                ` ${count(repeatedNumbers, "number")} ${
                  repeatedNumbers === 1 ? "appears" : "appear"
                } more than once: each is one customer, and every row is kept as an enquiry.`}{" "}
              Numbers already in your CRM are not duplicated, and importing the same file again adds nothing.
            </p>
            <button className="primary" disabled={!ready} onClick={() => void start()}>
              {progress ?? `Import ${prepared.length} leads`}
            </button>
            {(mode === "one" || mode === "split") && chosen.length === 0 && (
              <span className="faint" style={{ marginLeft: 8 }}>Choose who calls them.</span>
            )}
            {outcome && (
              <div style={{ marginTop: 12 }}>
                {outcome.stoppedAt && (
                  <div className="error-note">
                    {outcome.stoppedAt}. The rows before these are in; import the same file again to add the rest.
                  </div>
                )}
                <div className="info-note">
                  {importSummary(outcome)}
                  {(mode === "one" || mode === "split") && outcome.inserted > 0 &&
                    ` New customers went to ${names(chosen)}.`}
                </div>
                {outcome.problems.length > 0 && (
                  <>
                    <div className="row between">
                      <h3>Skipped rows</h3>
                      <button onClick={downloadSkipped}>Download the skipped rows</button>
                    </div>
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>Line</th>
                            <th>Phone</th>
                            <th>Why it was skipped</th>
                          </tr>
                        </thead>
                        <tbody>
                          {outcome.problems.slice(0, 200).map((p, i) => (
                            <tr key={i}>
                              <td className="num">{p.line}</td>
                              <td>{p.phone}</td>
                              <td>{p.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
                {outcome.notes.length > 0 && (
                  <>
                    <h3>Imported, with a note</h3>
                    <ul className="faint">
                      {outcome.notes.slice(0, 50).map((n, i) => <li key={i}>Line {n.line}: {n.note}.</li>)}
                    </ul>
                  </>
                )}
              </div>
            )}
          </Card>
        )}

        <Card title="Recent imports">
          <ErrorNote error={imports.error} />
          {imports.data && imports.data.length === 0 && <p className="faint">No imports yet.</p>}
          {imports.data && imports.data.length > 0 && (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>File</th>
                    <th>Source</th>
                    <th className="num">New</th>
                    <th className="num">Already known</th>
                    <th className="num">Enquiries</th>
                    <th className="num">Skipped</th>
                    <th>By</th>
                  </tr>
                </thead>
                <tbody>
                  {imports.data.map((i) => (
                    <tr key={i.id}>
                      <td>{fmtDateTime(i.created_at, tz)}</td>
                      <td>{i.file_name ?? "–"}{i.format === "facebook" ? " (Facebook)" : ""}</td>
                      <td>{sources.data?.find((s) => s.id === i.source_id)?.name ?? "–"}</td>
                      <td className="num">{i.inserted}</td>
                      <td className="num">{i.updated}</td>
                      <td className="num">{i.enquiries}</td>
                      <td className="num">{i.skipped}</td>
                      <td>{team.find((m) => m.id === i.created_by)?.display_name ?? "–"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </Page>
  );
}

/** Who gets the new customers: one person, or several who get them in turn. */
export function PersonPicker(
  props: { team: Member[]; chosen: string[]; onChange: (ids: string[]) => void; single?: boolean; name?: string },
) {
  const toggle = (id: string, on: boolean) =>
    props.onChange(
      props.single ? (on ? [id] : []) : on ? [...props.chosen, id] : props.chosen.filter((c) => c !== id),
    );
  return (
    <div className="chips" style={{ margin: "8px 0" }}>
      {props.team.map((m) => (
        <label className="row" key={m.id} style={{ marginRight: 12 }}>
          <input
            type={props.single ? "radio" : "checkbox"}
            name={props.name ?? "person"}
            checked={props.chosen.includes(m.id)}
            onChange={(e) => toggle(m.id, e.target.checked)}
          />
          {m.display_name}
          <span className="faint">{m.role === "caller" ? "" : ` (${m.role})`}</span>
        </label>
      ))}
    </div>
  );
}
